import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { useState } from 'react';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import './CustomerAnalytics.css';

const CustomerAnalytics = ({ customers, customerHealthStatus }) => {
  const [chartView, setChartView] = useState('all'); // 'all', 'distribution', 'health'

  // Calculate monthly new customers trend (last 6 months)
  const monthlyTrendData = useMemo(() => {
    if (customers.length === 0) return [];

    // Get current date and calculate 6 months ago
    const now = new Date();
    const months = [];

    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        count: 0,
        monthIndex: date.getMonth(),
        year: date.getFullYear()
      });
    }

    // Sort customers by ID (assuming higher ID = more recent)
    const sortedCustomers = [...customers].sort((a, b) => a.id - b.id);

    // Distribute customers evenly across the 6 months as an approximation
    // This is a rough estimate since we don't have actual creation dates
    const customersPerMonth = Math.ceil(sortedCustomers.length / 6);

    sortedCustomers.forEach((customer, index) => {
      const monthIndex = Math.floor(index / customersPerMonth);
      if (monthIndex < 6) {
        months[monthIndex].count++;
      }
    });

    return months;
  }, [customers]);

  // Calculate health status distribution
  const healthStatusData = useMemo(() => {
    const statusCounts = {
      healthy: 0,
      warning: 0,
      overdue: 0,
      none: 0
    };

    customers.forEach(customer => {
      const status = customerHealthStatus[customer.id] || 'none';
      statusCounts[status]++;
    });

    return [
      { name: 'Recent', value: statusCounts.healthy, color: '#10B981' },
      { name: 'Due Soon', value: statusCounts.warning, color: '#F59E0B' },
      { name: 'Overdue', value: statusCounts.overdue, color: '#EF4444' },
      { name: 'No History', value: statusCounts.none, color: '#9CA3AF' }
    ].filter(item => item.value > 0);
  }, [customers, customerHealthStatus]);

  // Calculate city distribution (top 10 cities)
  const cityData = useMemo(() => {
    const cityCounts = {};
    customers.forEach(customer => {
      const city = customer.custObject?.custCity;
      if (city) {
        // Normalize city name to title case for consistent grouping
        const normalizedCity = city
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
          .trim();
        cityCounts[normalizedCity] = (cityCounts[normalizedCity] || 0) + 1;
      }
    });

    return Object.entries(cityCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [customers]);

  // Calculate status distribution
  const statusData = useMemo(() => {
    const activeCount = customers.filter(c => c.status === 'Active').length;
    const inactiveCount = customers.filter(c => c.status === 'Inactive').length;

    return [
      { name: 'Active', value: activeCount, color: '#2196F3' },
      { name: 'Inactive', value: inactiveCount, color: '#9E9E9E' }
    ].filter(item => item.value > 0);
  }, [customers]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 1.5,
            border: '1px solid #e0e0e0',
            boxShadow: 2
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {payload[0].name}
          </Typography>
          <Typography variant="body2" color="primary">
            Count: {payload[0].value}
          </Typography>
          {payload[0].payload.percentage && (
            <Typography variant="caption" color="text.secondary">
              {payload[0].payload.percentage}%
            </Typography>
          )}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2, sm: 3 }, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShowChartIcon sx={{ color: '#003366', fontSize: { xs: 24, sm: 28 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#003366', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Customer Analytics
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={chartView}
          exclusive
          onChange={(e, newView) => newView && setChartView(newView)}
          size="small"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& .MuiToggleButton-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '4px 8px', sm: '6px 16px' },
              flex: { xs: 1, sm: 'initial' }
            }
          }}
        >
          <ToggleButton value="all">All Charts</ToggleButton>
          <ToggleButton value="distribution">Distribution</ToggleButton>
          <ToggleButton value="health">Health Status</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Monthly New Customers Trend Chart */}
        {(chartView === 'all' || chartView === 'distribution') && monthlyTrendData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#003366', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Monthly New Customers Trend
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666', fontStyle: 'italic', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Track your customer acquisition over the last 6 months. Shows business growth patterns and seasonal trends.
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      style={{ fontSize: '0.7rem' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      style={{ fontSize: '0.75rem' }}
                      allowDecimals={false}
                      domain={[0, 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#003366"
                      strokeWidth={3}
                      name="New Customers"
                      dot={{ fill: '#003366', r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Health Status Pie Chart */}
        {(chartView === 'all' || chartView === 'health') && healthStatusData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#003366', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Service Health Distribution
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666', fontStyle: 'italic', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Based on last service date. <strong style={{ color: '#10B981' }}>Recent</strong> (0-6 days), <strong style={{ color: '#F59E0B' }}>Due Soon</strong> (7-13 days), <strong style={{ color: '#EF4444' }}>Overdue</strong> (14+ days), <strong style={{ color: '#9CA3AF' }}>No History</strong> (never serviced).
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={healthStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      style={{ fontSize: '0.75rem' }}
                    >
                      {healthStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* City Distribution Chart */}
        {(chartView === 'all' || chartView === 'distribution') && cityData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#003366', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Top Cities by Customer Count
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666', fontStyle: 'italic', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Your top 10 cities by customer concentration. Identify key service areas and expansion opportunities.
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={cityData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" style={{ fontSize: '0.75rem' }} />
                    <YAxis dataKey="name" type="category" width={80} style={{ fontSize: '0.7rem' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#10B981" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Active vs Inactive Chart */}
        {(chartView === 'all' || chartView === 'distribution') && statusData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#003366', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Customer Status Overview
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666', fontStyle: 'italic', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Active customers appear in your default customer list. Inactive customers are hidden from regular operations.
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      style={{ fontSize: '0.75rem' }}
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {customers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No customer data available for analytics
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CustomerAnalytics;
